// FlightDetailPage — Fare selection step of the booking funnel.
// Dark palette: dark.bg #0A0F1C, dark.surface #131C30, flame #FF5A3C accents.
// TODO(booking-API-PR): wire to GET /flights/:offerId for real flight + fares.

import { useNavigate } from 'react-router-dom';
import { BookingProgressBar } from '@/components/BookingProgressBar';
import { FareCard } from '@/components/FareCard';
import { PriceBreakdown } from '@/components/PriceBreakdown';
import { Button } from '@/components/primitives';
import { BookingProvider, useBooking } from '@/lib/booking-store';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  FARE_TIERS,
  MOCK_FLIGHT,
  type FareTier,
} from '@/lib/booking-mock';

// ─── FARE FULL BREAKDOWN ─────────────────────────────────────────────────────
function fareIsGood(val: string): boolean {
  return (
    !val.startsWith('No ') &&
    !val.startsWith('Not ') &&
    !val.startsWith('Paid') &&
    val !== 'No refund'
  );
}

const BREAKDOWN_KEYS: Array<{ label: string; key: keyof FareTier }> = [
  { label: 'Cabin bag',       key: 'cabin' },
  { label: 'Checked bag',     key: 'bag' },
  { label: 'Seat selection',  key: 'seat' },
  { label: 'Date changes',    key: 'change' },
  { label: 'Cancellation',    key: 'cancel' },
  { label: 'Meals',           key: 'meal' },
];

// ─── INNER (needs BookingProvider context) ────────────────────────────────────
function FlightDetailInner() {
  const navigate               = useNavigate();
  const isMobile               = useIsMobile();
  const { state, dispatch }    = useBooking();
  const selectedFare           = state.selectedFare;

  const f = MOCK_FLIGHT;

  function handleSelectFare(fare: FareTier) {
    dispatch({ type: 'SET_FARE', fare });
  }

  function handleContinue() {
    navigate('/passengers');
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <BookingProgressBar step="flight-detail" />

      <div style={{ padding: '32px clamp(16px, 4vw, 64px) 80px', maxWidth: 1280, margin: '0 auto' }}>

        {/* ── Flight header card ─────────────────────────────────────────── */}
        <div
          className="bg-[#131C30] rounded-[20px] border border-white/[0.07] mb-8"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '220px 1fr 200px',
            alignItems: 'center',
            gap: 24,
            padding: '20px 28px',
          }}
        >
          {/* Airline */}
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center text-white text-[13px] font-bold"
              style={{ background: f.logoColor }}
            >
              {f.code}
            </div>
            <div>
              <div className="text-[15px] font-bold text-[#F4F3F0]"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                {f.airline}
              </div>
              <div className="text-[12px] text-[#8C98B0] mt-0.5"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                {f.flightNo} · {f.aircraft}
              </div>
            </div>
          </div>

          {/* Route timeline */}
          <div className="flex items-center gap-4">
            {/* Depart */}
            <div className="text-center">
              <div className="text-[30px] font-bold text-[#F4F3F0] leading-none"
                   style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}>
                {f.depart}
              </div>
              <div className="text-[13px] font-semibold text-[#F4F3F0] mt-0.5"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                {f.from}
              </div>
              <div className="text-[11px] text-[#8C98B0]"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                {f.date}
              </div>
            </div>

            {/* Connector */}
            <div className="flex-1 relative px-2">
              <div className="h-px bg-white/[0.14] relative">
                <div className="absolute left-0 -top-[5px] w-2.5 h-2.5 rounded-full bg-[#FF5A3C]" />
                <div className="absolute right-0 -top-[5px] w-2.5 h-2.5 rounded-full bg-[#FF5A3C]" />
              </div>
              <div className="text-center mt-2.5 text-[12px] text-[#8C98B0] font-medium"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                {f.duration} · {f.stops}
              </div>
            </div>

            {/* Arrive */}
            <div className="text-center">
              <div className="text-[30px] font-bold text-[#F4F3F0] leading-none"
                   style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}>
                {f.arrive}
              </div>
              <div className="text-[13px] font-semibold text-[#F4F3F0] mt-0.5"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                {f.to}
              </div>
              <div className="text-[11px] text-[#8C98B0]"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                {f.arriveDate}
              </div>
            </div>
          </div>

          {/* Score / eco */}
          <div className="text-right">
            <div className="text-[11px] font-bold text-[#FF5A3C] tracking-[0.5px]"
                 style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
              ● BEST VALUE
            </div>
            <div className="text-[12px] text-[#8C98B0] mt-1.5"
                 style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
              🌿 {f.co2Label}
            </div>
            <div className="text-[12px] text-[#8C98B0] mt-0.5"
                 style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
              {f.score}/100 score
            </div>
          </div>
        </div>

        {/* ── Two-column layout ──────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 28 }}>

          {/* Left column */}
          <div>
            <h2
              className="text-[28px] font-semibold text-[#F4F3F0] mb-5 tracking-tight"
              style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif', letterSpacing: '-0.02em' }}
            >
              Choose your fare
            </h2>

            {/* 4-fare card matrix */}
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 28 }}
            >
              {FARE_TIERS.map(fare => (
                <FareCard
                  key={fare.k}
                  fare={fare}
                  selected={selectedFare.k === fare.k}
                  onClick={() => handleSelectFare(fare)}
                />
              ))}
            </div>

            {/* Full fare breakdown */}
            <div className="bg-[#131C30] rounded-[18px] border border-white/[0.07] p-[22px]">
              <div
                className="text-[11px] font-bold tracking-[1.5px] text-[#8C98B0] uppercase mb-4"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                {selectedFare.label} fare — full breakdown
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                {BREAKDOWN_KEYS.map(({ label, key }) => {
                  const val = selectedFare[key] as string;
                  const ok  = key === 'cabin' || fareIsGood(val);
                  return (
                    <div
                      key={label}
                      className="rounded-xl p-3"
                      style={{
                        background:   ok ? 'rgba(255,90,60,0.06)' : 'rgba(255,255,255,0.03)',
                        border:       `1px solid ${ok ? 'rgba(255,90,60,0.25)' : 'rgba(255,255,255,0.07)'}`,
                      }}
                    >
                      <div
                        className="text-[10px] font-bold uppercase text-[#8C98B0] mb-1 tracking-[1px]"
                        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                      >
                        {label}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span
                          className="text-[13px] font-bold"
                          style={{ color: ok ? '#FF5A3C' : '#4E5E78' }}
                        >
                          {ok ? '✓' : '—'}
                        </span>
                        <span
                          className="text-[12px]"
                          style={{
                            fontFamily: '"Inter Variable","Inter",system-ui,sans-serif',
                            color: ok ? '#F4F3F0' : '#8C98B0',
                          }}
                        >
                          {val}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="sticky top-[64px] self-start flex flex-col gap-3">
            <PriceBreakdown
              fareLabel={`${f.airline} · ${selectedFare.label}`}
              routeLabel={`${f.from} → ${f.to} · Mar 17 · ${f.duration}`}
              fareBasePaise={selectedFare.pricePaise}
              sectionTitle="YOUR SELECTION"
              cta={
                <Button
                  variant="flame"
                  size="lg"
                  full
                  onClick={handleContinue}
                  className="rounded-[14px] font-bold"
                >
                  Continue to passengers →
                </Button>
              }
              ctaNote="Free cancellation until Mar 14"
            />

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-2">
              {['🔒 Secure', '24h support', '✓ Best price'].map(b => (
                <div
                  key={b}
                  className="py-2 px-1 rounded-[10px] bg-[#131C30] border border-white/[0.07] text-[11px] text-[#8C98B0] text-center"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  {b}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EXPORT (wraps with BookingProvider) ─────────────────────────────────────
export function FlightDetailPage() {
  return (
    <BookingProvider>
      <FlightDetailInner />
    </BookingProvider>
  );
}
